import { BigNumber } from 'ethers';
import { useCallback, useMemo, useState } from 'react';
import { useTransactionAdder } from '../state/transactions/hooks';
import { useDoUpdate, useOnchainInteractionsLendingPool } from './useOnchain';
import { ButtonState } from '../components/InteractionButton';
import { PermitData } from './useApprove';
import { useToNumber } from './useConverter';
import { formatFloat } from '../utils/format';
import useGenericCall from './useGenericCall';
import { useOffchainPoolToken } from './useOffchain';


export default function useDeleverage(
  approvalState: ButtonState,
  invalidInput: boolean, 
  tokens: BigNumber,
  amountAMin: BigNumber, 
  amountBMin: BigNumber,
  permitData: PermitData
): [ButtonState, () => Promise<void>] {
  const onchainInteractionsLendingPool = useOnchainInteractionsLendingPool();
  const doUpdate = useDoUpdate();
  const addTransaction = useTransactionAdder();
  const [pending, setPending] = useState<boolean>(false);

  const val = useToNumber(tokens);
  const symbol = useGenericCall(useOffchainPoolToken(), poolToken => poolToken.getSymbol());
  const summary = `Deleverage ${symbol}: withdraw ${formatFloat(val)} ${symbol}`;
  
  const deleverageState: ButtonState = useMemo(() => {
    if (invalidInput) return ButtonState.Disabled;
    if (approvalState != ButtonState.Done) return ButtonState.Disabled;
    if (pending) return ButtonState.Pending;
    return ButtonState.Ready;
  }, [approvalState, pending]);

  const deleverage = useCallback(async (): Promise<void> => {
    if (deleverageState !== ButtonState.Ready) return;
    setPending(true);
    try {
      await onchainInteractionsLendingPool.deleverage(tokens, amountAMin, amountBMin, permitData, (hash: string) => {
        addTransaction({ hash }, { summary });
      });
      doUpdate();
    }
    finally {
      setPending(false);
    }
  }, [onchainInteractionsLendingPool, addTransaction, tokens, amountAMin, amountBMin, permitData, deleverageState]);

  return [deleverageState, deleverage];
}